import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SearchFilterOptionType } from '../../data/types';
import { useCompleteSignIn, useConsumePartnerClaim, useLoadPartnerClaim } from '../../hooks/authhooks';

type PartnerClaimProps = {
  searchFilterOptions: SearchFilterOptionType
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Contact name',
  email: 'Email',
  phone: 'Phone',
  business_name: 'Business name',
  city: 'City',
  state: 'State',
  reason: 'Referral reason',
  next_action: 'Suggested next action',
};

const unavailableMessage = (status?: string) => {
  if (status === 'consumed') return 'This connection has already been used.';
  return 'This connection link is invalid or has expired.';
};

/** Auth-gated landing page for an opaque AiriWheels provider claim. */
const PartnerClaim: React.FC<PartnerClaimProps> = ({ searchFilterOptions }) => {
  const { claimToken = '' } = useParams();
  const { user } = useCompleteSignIn();
  const authenticatedUid = user?.uid || '';
  const isAuthenticated = Boolean(authenticatedUid && (user?.username || user?.email));
  const { preview, isLoadingPartnerClaim, isPartnerClaimError } = useLoadPartnerClaim(
    authenticatedUid,
    claimToken,
    isAuthenticated
  );
  const { consumeClaim, consumeResult, isConsuming, isConsumeError } = useConsumePartnerClaim(
    authenticatedUid,
    claimToken
  );
  const [confirmedPrefill, setConfirmedPrefill] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const availablePreview = preview?.status === 'available' ? preview : null;
  const previewError = isPartnerClaimError
    ? 'We could not load this connection. The link may be invalid or expired.'
    : (!availablePreview && preview ? unavailableMessage(preview.status) : '');

  const handleConsume = async () => {
    if (!isAuthenticated || !confirmedPrefill || !acceptedTerms || isConsuming) return;
    try {
      await consumeClaim();
    } catch {
      // Consume errors stay on this page; the claim is not treated as used.
    }
  };

  if (!isAuthenticated) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center px-4">
        <section className="page-card max-w-2xl w-full p-6" data-testid="claim-auth-required">
          <p className="page-kicker">AiriWheels referral</p>
          <h1 className="font-raleway text-2xl font-semibold text-ink my-2">Connect to HomeCook securely</h1>
          <p className="font-open-sans text-[#3F5B64] mb-5">
            Sign in or create a HomeCook account to verify your identity and review the information shared with your consent.
          </p>
          <button
            type="button"
            className="h-12 rounded-lg bg-primaryDark-0 px-6 font-bold font-raleway text-white"
            onClick={() => searchFilterOptions.setSignInModalIsOpen(true)}
          >
            Sign in to continue
          </button>
        </section>
      </main>
    );
  }

  if (isLoadingPartnerClaim) {
    return <main className="min-h-[60vh] flex items-center justify-center font-open-sans" data-testid="claim-loading">Loading secure connection…</main>;
  }

  if (previewError && !availablePreview) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center px-4">
        <section className="page-card max-w-2xl w-full p-6" data-testid="claim-error">
          <p className="page-kicker">Connect HomeCook</p>
          <h1 className="font-raleway text-2xl font-semibold text-ink my-2">Connection unavailable</h1>
          <p className="font-open-sans text-[#3F5B64]">{previewError}</p>
        </section>
      </main>
    );
  }

  if (consumeResult) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center px-4">
        <section className="page-card max-w-2xl w-full p-6" data-testid="claim-success">
          <p className="page-kicker">Connected</p>
          <h1 className="font-raleway text-2xl font-semibold text-ink my-2">Continue your HomeCook provider setup</h1>
          <p className="font-open-sans text-[#3F5B64] mb-5">
            Your referral is connected. Complete your facility, capacity, equipment, availability, pricing, access, and
            documentation details in HomeCook.
          </p>
          <a className="inline-flex h-12 items-center rounded-lg bg-primaryDark-0 px-6 font-bold font-raleway text-white" href={consumeResult.onboarding_url}>
            Continue facility onboarding
          </a>
        </section>
      </main>
    );
  }

  const fields = Object.entries(availablePreview?.consented_fields || {}).filter(([, value]) => Boolean(value));

  return (
    <main className="min-h-[60vh] flex items-center justify-center px-4 py-8">
      <section className="page-card max-w-2xl w-full p-6" data-testid="partner-claim">
        <p className="page-kicker">AiriWheels referral</p>
        <h1 className="font-raleway text-2xl font-semibold text-ink my-2">Activate available kitchen capacity</h1>
        <p className="font-open-sans text-[#3F5B64] mb-5">
          Review the information AiriWheels shared with your consent, then connect it to your HomeCook account.
        </p>
        <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-x-3 gap-y-2 rounded-xl border border-[#E4EEF1] p-4 mb-4">
          {fields.map(([field, value]) => (
            <React.Fragment key={field}>
              <dt className="font-raleway font-semibold text-ink">{FIELD_LABELS[field] || field}</dt>
              <dd className="font-open-sans text-[#3F5B64] break-words">{value}</dd>
            </React.Fragment>
          ))}
        </dl>
        <div className="rounded-lg bg-primary-tint p-3 mb-4 font-open-sans text-sm text-[#3F5B64]" data-testid="non-regulatory-copy">
          Connecting starts provider onboarding. It does not mean that HomeCook has approved the facility, confirmed
          compliance, or determined eligibility. Facility information is completed in HomeCook and is never inferred
          from this referral.
        </div>
        <>
          <label className="flex items-start gap-2 mb-3 font-open-sans text-sm">
            <input type="checkbox" checked={confirmedPrefill} onChange={(event) => setConfirmedPrefill(event.target.checked)} data-testid="confirm-prefill" />
            <span>I confirm that the information shown above may prefill my HomeCook provider account.</span>
          </label>
          <label className="flex items-start gap-2 mb-4 font-open-sans text-sm">
            <input type="checkbox" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)} data-testid="accept-homecook-terms" />
            <span>
              I agree to the HomeCook <Link className="underline text-primaryDark-0" to="/terms">Terms</Link> and acknowledge the{' '}
              <Link className="underline text-primaryDark-0" to="/privacy">Privacy Policy</Link>.
            </span>
          </label>
          {isConsumeError && <p className="text-red-500 font-open-sans text-sm mb-3" data-testid="claim-consume-error">We could not connect this referral. Please retry; the claim was not consumed by this attempt.</p>}
          <button
            type="button"
            disabled={!confirmedPrefill || !acceptedTerms || isConsuming}
            onClick={handleConsume}
            data-testid="consume-claim"
            className={`h-12 rounded-lg px-6 font-bold font-raleway text-white ${(!confirmedPrefill || !acceptedTerms || isConsuming) ? 'bg-gray-300' : 'bg-primaryDark-0'}`}
          >
            {isConsuming ? 'Connecting…' : 'Connect and continue'}
          </button>
        </>
      </section>
    </main>
  );
};

export default PartnerClaim;
